import {
  KeyboardEvent,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Variable } from "../../models/interactions/Variable";

export interface SuggestionContext {
  query: string;
  replaceStart: number;
  replaceEnd: number;
}

export function getSuggestionContext(
  value: string,
  selectionStart: number
): SuggestionContext | null {
  if (selectionStart < 0 || selectionStart > value.length) return null;

  const beforeCaret = value.slice(0, selectionStart);
  const dollarIndex = beforeCaret.lastIndexOf("$");
  if (dollarIndex === -1) return null;

  const query = beforeCaret.slice(dollarIndex + 1);
  if (query.includes("]") || query.includes("[") || /\s/.test(query)) {
    return null;
  }

  const hasBracketPrefix =
    dollarIndex > 0 && beforeCaret[dollarIndex - 1] === "[";

  return {
    query,
    replaceStart: hasBracketPrefix ? dollarIndex - 1 : dollarIndex,
    replaceEnd: selectionStart,
  };
}

interface UseVariableIntellisenseProps {
  enabled: boolean;
  variables: Variable[];
  editableValue: string;
  setEditableValue: (value: string) => void;
  textareaRef: RefObject<HTMLTextAreaElement>;
  persistValue: (value: string) => void;
}

export default function useVariableIntellisense({
  enabled,
  variables,
  editableValue,
  setEditableValue,
  textareaRef,
  persistValue,
}: UseVariableIntellisenseProps) {
  const [isSuggestionOpen, setIsSuggestionOpen] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const [suggestionQuery, setSuggestionQuery] = useState("");
  const [replaceStart, setReplaceStart] = useState(0);
  const [replaceEnd, setReplaceEnd] = useState(0);
  const suggestionMenuRef = useRef<HTMLDivElement>(null);

  const filteredSuggestions = useMemo(() => {
    if (!enabled || !isSuggestionOpen) return [];
    const normalizedQuery = suggestionQuery.toLowerCase();
    return variables.filter((variable) =>
      variable.name.toLowerCase().startsWith(normalizedQuery)
    );
  }, [enabled, isSuggestionOpen, suggestionQuery, variables]);

  const closeSuggestionMenu = useCallback(() => {
    setIsSuggestionOpen(false);
    setSelectedSuggestionIndex(0);
    setSuggestionQuery("");
  }, []);

  const updateSuggestionState = useCallback(
    (value: string, selectionStart: number | null | undefined) => {
      if (!enabled) {
        closeSuggestionMenu();
        return;
      }

      const caret = selectionStart ?? value.length;
      const context = getSuggestionContext(value, caret);

      if (!context) {
        closeSuggestionMenu();
        return;
      }

      setReplaceStart(context.replaceStart);
      setReplaceEnd(context.replaceEnd);
      setSuggestionQuery(context.query);
      setIsSuggestionOpen(true);
      setSelectedSuggestionIndex(0);
    },
    [enabled, closeSuggestionMenu]
  );

  const insertVariable = useCallback(
    (variable: Variable) => {
      const token = `[$${variable.name}]`;
      const nextValue =
        editableValue.slice(0, replaceStart) +
        token +
        editableValue.slice(replaceEnd);

      setEditableValue(nextValue);
      persistValue(nextValue);
      closeSuggestionMenu();

      requestAnimationFrame(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;
        const nextCaretPosition = replaceStart + token.length;
        textarea.focus();
        textarea.setSelectionRange(nextCaretPosition, nextCaretPosition);
      });
    },
    [
      editableValue,
      replaceStart,
      replaceEnd,
      setEditableValue,
      persistValue,
      closeSuggestionMenu,
      textareaRef,
    ]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (!enabled || !isSuggestionOpen || filteredSuggestions.length === 0) {
        return;
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedSuggestionIndex(
          (prev) => (prev + 1) % filteredSuggestions.length
        );
        return;
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedSuggestionIndex((prev) =>
          prev === 0 ? filteredSuggestions.length - 1 : prev - 1
        );
        return;
      }

      if (e.key === "Enter") {
        e.preventDefault();
        const selectedVariable =
          filteredSuggestions[selectedSuggestionIndex] ?? filteredSuggestions[0];
        if (selectedVariable) {
          insertVariable(selectedVariable);
        }
        return;
      }

      if (e.key === "Escape") {
        e.preventDefault();
        closeSuggestionMenu();
      }
    },
    [
      enabled,
      isSuggestionOpen,
      filteredSuggestions,
      selectedSuggestionIndex,
      insertVariable,
      closeSuggestionMenu,
    ]
  );

  useEffect(() => {
    if (!isSuggestionOpen) return;
    if (filteredSuggestions.length === 0) {
      closeSuggestionMenu();
      return;
    }

    if (selectedSuggestionIndex >= filteredSuggestions.length) {
      setSelectedSuggestionIndex(0);
    }
  }, [
    isSuggestionOpen,
    filteredSuggestions,
    selectedSuggestionIndex,
    closeSuggestionMenu,
  ]);

  useEffect(() => {
    if (!isSuggestionOpen) return;
    const suggestionMenu = suggestionMenuRef.current;
    if (!suggestionMenu) return;

    const selectedElement = suggestionMenu.querySelector<HTMLElement>(
      `[data-suggestion-index="${selectedSuggestionIndex}"]`
    );

    if (selectedElement?.scrollIntoView) {
      selectedElement.scrollIntoView({
        block: "nearest",
      });
    }
  }, [isSuggestionOpen, selectedSuggestionIndex, filteredSuggestions.length]);

  return {
    isSuggestionOpen,
    selectedSuggestionIndex,
    filteredSuggestions,
    suggestionMenuRef,
    closeSuggestionMenu,
    updateSuggestionState,
    insertVariable,
    handleKeyDown,
  };
}
